import { IMatchingOrder, IProgressiveDeductible } from "types/Order/Order";

// Form-context fields the Benefits section reads to render the "Found N previous orders" banner
// and apply / undo the reductions. One writer (useMatchingOrders) sets them; everything else reads.
export interface IMatchingOrderRow {
  id: string;
  dateOfService: string;
  amountInCents: number;
  deductibleReductionInCents: number;
  oopReductionInCents: number;
  patientFirstName: string;
  patientLastName: string;
  orderStatus: string;
  accountId: string;
}

export interface IMatchingOrdersFormValues {
  hasMatchingOrders: boolean;
  deductibleReductionAmount: number;
  oopReductionAmount: number;
  matchingOrdersData: IMatchingOrderRow[];
}

export const EMPTY_MATCHING_ORDERS: IMatchingOrdersFormValues = {
  hasMatchingOrders: false,
  deductibleReductionAmount: 0,
  oopReductionAmount: 0,
  matchingOrdersData: [],
};

/**
 * Turns the backend's progressive-deductible answer into the form values the banner renders.
 * Amounts come back in cents and the benefit fields are in dollars, hence the /100.
 * Lifted verbatim from GFEServiceDetails (CLE-2769 follow-up), where it only ran as part of the
 * price calculation and therefore needed a CPT first.
 */
export function buildMatchingOrdersFormValues(
  progressiveDeductible: IProgressiveDeductible | undefined | null
): IMatchingOrdersFormValues {
  if (!progressiveDeductible) {
    return EMPTY_MATCHING_ORDERS;
  }

  const {
    totalDeductibleForMatchingOrders,
    totalOutOfPocketForMatchingOrders,
    matchingOrders = [],
  } = progressiveDeductible;

  const deductibleReductionAmount = totalDeductibleForMatchingOrders / 100;
  const oopReductionAmount = totalOutOfPocketForMatchingOrders / 100;

  const matchingOrdersData = matchingOrders.map((order: IMatchingOrder) => {
    let deductibleReductionInCents = 0;
    let oopReductionInCents = 0;

    const procedureBreakdown =
      order.gfeQuote?.pricingBreakdown?.procedureBreakdown;
    if (procedureBreakdown) {
      procedureBreakdown.forEach((proc) => {
        if (proc.owedByPatient) {
          deductibleReductionInCents += proc.owedByPatient.deductible || 0;
          const total = proc.owedByPatient.total || 0;
          const selfPay = proc.owedByPatient.selfPay || 0;
          oopReductionInCents += total - selfPay;
        }
      });
    } else {
      // No stored breakdown: attribute the totals by the order's share of the amount.
      const orderContributionRatio =
        order.amountInCents / totalDeductibleForMatchingOrders;
      deductibleReductionInCents = Math.round(
        totalDeductibleForMatchingOrders * orderContributionRatio
      );
      oopReductionInCents = Math.round(
        totalOutOfPocketForMatchingOrders * orderContributionRatio
      );
    }

    return {
      id: order.id,
      dateOfService: order.dateOfService,
      amountInCents: order.amountInCents,
      deductibleReductionInCents,
      oopReductionInCents,
      patientFirstName: order.patient.firstName,
      patientLastName: order.patient.lastName,
      orderStatus: order.status,
      accountId: order.accountId,
    };
  });

  return {
    hasMatchingOrders: deductibleReductionAmount > 0 || oopReductionAmount > 0,
    deductibleReductionAmount,
    oopReductionAmount,
    matchingOrdersData,
  };
}

// The form keeps dates as MM/DD/YYYY strings while the user types; only a complete one is worth
// sending. Same check GFEServiceDetails applies before pricing.
const MM_DD_YYYY = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
export function isCompleteFormDate(dateStr: unknown): dateStr is string {
  return typeof dateStr === "string" && MM_DD_YYYY.test(dateStr);
}
